import React, { useState, useEffect, useMemo } from "react";
import { Label } from "flowbite-react";
import CustomButton from '../../../components/common/CustomButton';
import FormInput from '../../../components/common/FormInput';
import NumberInput from '../../../components/common/NumberInput';
import ColorPicker from '../../../components/common/ColorPicker';
import { Drawer } from "../../../components/Modals";
import { useJwt } from "../../../helpers/common";
import { useSelector } from "react-redux";
import toast from 'react-hot-toast';
import { HiTrash } from "react-icons/hi";
import ConfirmDeleteModal from '../../../components/Modals/ConfirmDeleteModal';
import {useOrganizationService} from "../../../services";
import {SkillsPayload} from "../types";
import UnsavedChangesIndicator from '../../../components/common/UnsavedChangesIndicator';
import CacheResetAlert from "../../../features/CacheResetAlert/CacheResetAlert";

interface OrgSkillsSkillModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: any;
}

const OrgSkillsModal = ({ isOpen, onClose, currentData }: OrgSkillsSkillModalProps) => {
  const { updateSkills, addSkills, deleteOrganizationSkill } = useOrganizationService();
  const {
    skillLoading
  } = useSelector((state: any) => state.organization)

  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;
  const [payload, setPayload] = useState<SkillsPayload>({
    skill_cost: '',
    skill: '',
    description: '',
    id: 0,
    cost_per_hour: 0,
    color_code: "#182439"
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCostResyncMessage, setShowCostResyncMessage] = useState(false);
  const normalizeSkill = (s: string | undefined | null) => (s || '').toUpperCase().trim();
  const originalIsDefault = Boolean(currentData?.id && normalizeSkill(currentData?.skill) === 'DEFAULT');

  useEffect(() => {
    setPayload(currentData);
  }, [currentData]);

  // Reset resync message when modal opens/closes or data changes
  useEffect(() => {
    setShowCostResyncMessage(false);
  }, [isOpen, currentData]);

  const hasUnsavedChanges = useMemo(() => {
    if (!currentData?.id) {
      return false;
    }
    return JSON.stringify(payload) !== JSON.stringify(currentData);
  }, [currentData, payload]);

  // Detect if cost or billing changed (only relevant for edits)
  const hasCostChanged = useMemo(() => {
    if (!currentData?.id) return false;
    const costChanged = Number(payload?.cost_per_hour || 0) !== Number(currentData?.cost_per_hour || 0);
    const billingChanged = Number(payload?.billing_per_hour || 0) !== Number(currentData?.billing_per_hour || 0);
    return costChanged || billingChanged;
  }, [currentData, payload]);

  const handleOnChnage = (e: any) => {
    const { name, value } = e.target;
    if (originalIsDefault && (name === 'skill' || name === 'description')) {
      return;
    }

    if (name === "skill" && value) {
      setPayload({ ...payload, [name]: value.toUpperCase() });
    } else if (name === "cost_per_hour" || name === "billing_per_hour") {
      if (value === '') {
        setPayload({ ...payload, [name]: undefined });
      } else {
        const numValue = Number(value);
        if (numValue < 0) return;
        if (numValue > 10000) {
          setPayload({ ...payload, [name]: 10000 });
        } else {
          setPayload({ ...payload, [name]: numValue });
        }
      }
    } else if(value === "color_code") {
      setPayload({ ...payload, color_code: value });
      handleColorChange(value);
    } else {
      setPayload({ ...payload, [name]: value });
    }
  };

  const handleColorChange = (color: string) => {
    setPayload({ ...payload, color_code: color });
  }

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (originalIsDefault) {
      toast.error('Default skill cannot be deleted');
      setShowDeleteModal(false);
      return;
    }
    if (!payload.id) return;
    await deleteOrganizationSkill(org_id, payload.id.toString());
    setShowDeleteModal(false);
    onClose();
  };

  const onSubmit = async () => {
    const normalizedSkill = normalizeSkill(payload?.skill);
    if (!originalIsDefault && normalizedSkill === 'DEFAULT') {
      toast.error('Skill already exists!', { duration: 1000 });
      return;
    }

    if (!originalIsDefault && !payload.description) {
      toast.error("Please enter job duties!", { duration: 1000 });
      return;
    }

    if (isNaN(payload.cost_per_hour) || payload.cost_per_hour < 0) {
      toast.error("Please enter a valid cost per hour!", { duration: 1000 });
      return;
    }

    const submissionPayload = {
      ...payload,
      skill: normalizedSkill,
      cost_per_hour: payload.cost_per_hour.toString(),
    };

    if (!payload.color_code) {
      toast.error("Please select a color for skill!", { duration: 1000 });
      return;
    }

    if (payload.id) {
      const finalPayload = {
        ...submissionPayload,
        id: submissionPayload.id ? submissionPayload.id.toString() : '',
        billing_per_hour: payload.billing_per_hour !== undefined ? payload.billing_per_hour?.toString() : "0"
      };
      await updateSkills(org_id, finalPayload);

      // Show resync message only AFTER API responds successfully
      if (hasCostChanged) {
        setShowCostResyncMessage(true);
      } else {
        onClose();
      }
    } else {
      const finalPayload = {
        ...submissionPayload,
        id: submissionPayload.id ? submissionPayload.id.toString() : '',
        billing_per_hour: payload.billing_per_hour !== undefined ? payload.billing_per_hour.toString() : "0"
      };
      await addSkills(org_id, finalPayload);
      onClose();
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} position="right" size="42" minsize="42">
      <div
        className="relative h-full overflow-auto flex flex-col"
      >
        {/* Header */}
        <div className="flex flex-col border-b bg-light-surfacePrimary border-gray-500 dark:border-gray-700 !p-4 !pl-6 !pt-5
        dark:bg-tertiary-dark sticky top-0 z-40 shadow-lg rounded-b-2xl">
          <div className="flex w-full items-start justify-between mb-2">
            <div className="flex flex-col min-w-0 max-w-full">
              <div className="text-light-text-primary dark:text-white text-lg font-semibold font-['Inter'] leading-tight truncate pr-2">
                {payload?.id ? 'Edit Skill' : 'Add New Skill'}
              </div>
              <div className="text-light-text-tertiary dark:text-white/60 text-sm mt-1">
                {payload?.id ? 'Update the skill details below.' : 'Fill in the details to add a new skill.'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {payload?.id && !originalIsDefault && (
                <button
                  type="button"
                  className="text-red-600 hover:text-red-800 p-2 rounded-lg transition-colors duration-200"
                  title="Delete Skill"
                  onClick={handleDelete}
                >
                  <HiTrash className="w-5 h-5" />
                </button>
              )}
              <button
                type="button"
                className="text-light-text-secondary hover:text-light-text-primary dark:text-white/70 dark:hover:text-white
                hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
                onClick={onClose}>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col px-16 py-8 bg-light-surfacePrimary dark:bg-tertiary-dark">
          {/* Cost Resync Message - at the top of the form body */}
          {showCostResyncMessage && (
            <div className="mb-6">
              <CacheResetAlert
                title="Skill cost updated successfully!"
                description="This change will reflect across all your work items within 24 hours during the next auto-sync."
                buttonText="Update Cost Across Work Items"
                onClose={() => {
                  setShowCostResyncMessage(false);
                  onClose();
                }}
              />
            </div>
          )}

          <FormInput
            id="skill"
            name="skill"
            label="Skill"
            value={payload?.skill}
            onChange={handleOnChnage}
            placeholder="Skill"
            required
            showAsterisk
            disabled={originalIsDefault || showCostResyncMessage}
            className="uppercase !bg-gray-100 dark:!bg-[#374151]"
          />
          <FormInput
            id="description"
            name="description"
            label="Job Duties"
            type="textarea"
            value={payload?.description}
            onChange={handleOnChnage}
            placeholder="Enter job duties"
            maxLength={100}
            required
            showAsterisk
            counter
            disabled={originalIsDefault || showCostResyncMessage}
            className="!bg-gray-100 dark:!bg-[#374151]"
          />
          <NumberInput
            id="cost_per_hour"
            name="cost_per_hour"
            label="Cost Per Hour"
            value={payload?.cost_per_hour}
            onChange={handleOnChnage}
            placeholder="Cost Per Hour"
            min={0}
            max={10000}
            required
            showAsterisk
            disabled={showCostResyncMessage}
            className="!bg-gray-100 dark:!bg-[#374151]"
          />
          <NumberInput
            id="billing_per_hour"
            name="billing_per_hour"
            label="Billing Per Hour"
            value={payload?.billing_per_hour}
            onChange={handleOnChnage}
            placeholder="Bill Per Hour"
            min={0}
            max={10000}
            disabled={showCostResyncMessage}
            className="!bg-gray-100 dark:!bg-[#374151]"
          />
          <div className="w-full">
            <Label className="flex">
              <span>Select Skill Color</span>
              <span className="ml-1">*</span>
            </Label>
            <ColorPicker
              value={payload?.color_code}
              onChange={handleColorChange}
              position="top"
            />
          </div>
        </div>

        {!showCostResyncMessage && (
          <div className="px-4 py-4 items-center flex gap-4 mt-auto border-t rounded-t-md border-gray-500 dark:border-gray-700
          bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg sticky bottom-0">
            <CustomButton
              variant="cancel"
              onClick={onClose}
              className="hover:bg-light-interactive-hover dark:hover:bg-hover-dark transition-all duration-200"
            >
              Cancel
            </CustomButton>
            <UnsavedChangesIndicator hasUnsavedChanges={hasUnsavedChanges} className="ml-auto" />
            <CustomButton
              onClick={onSubmit}
              disabled={
                skillLoading
                || (!originalIsDefault && (!normalizeSkill(payload.skill) 
                || !payload.description))
                || isNaN(payload.cost_per_hour)
              }
              isLoading={skillLoading}
              className={hasUnsavedChanges ? '' : 'ml-auto'}
            >
              Save
            </CustomButton>
          </div>
        )}
      </div>

      {/* Delete Skill Modal */}
      <ConfirmDeleteModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        handleConfirm={handleConfirmDelete}
        title="Delete Skill"
        description={`Are you sure you want to delete "${payload?.skill || 'this skill'}"? This action cannot be undone.`}
        confirmText="Delete"
      />
    </Drawer>
  );
};

export default OrgSkillsModal;